import React, { useEffect, useState } from "react";
import AddShoppingCartOutlinedIcon from "@mui/icons-material/AddShoppingCartOutlined";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import {
  Alert,
  Box,
  Button,
  Card,
  Pagination,
  Snackbar,
  Typography,
} from "@mui/material";
import {
  Link,
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router-dom";
import { useDispatch } from "react-redux";
import { addToCart } from "../../store";
import { productToCartItem } from "../utils/productToCartItem";
import { fetchProductsPage, findBrandById } from "../utils/shopApi";

const PRODUCT_CARD_RED = "#e31837";
const PRODUCT_LIST_PAGE_SIZE = 20;

const pageShell = (children) => (
  <Box
    sx={{
      pt: "clamp(96px, 12vh, 136px)",
      pb: { xs: 4, md: 6 },
      px: { xs: 1.5, md: 2.5 },
      maxWidth: 1400,
      mx: "auto",
    }}
  >
    {children}
  </Box>
);

const getProductImage = (product) => {
  if (product?.image_url) return product.image_url;
  if (typeof product?.image === "string" && product.image.startsWith("http")) {
    return product.image;
  }
  if (typeof product?.image === "string" && product.image) {
    return `https://api.uktomars.co.uk/storage/${product.image}`;
  }
  return "";
};

const BrandProductsPage = () => {
  const dispatch = useDispatch();
  const { brandId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [items, setItems] = useState([]);
  const [brandName, setBrandName] = useState(location.state?.brand?.name ?? "");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [listMeta, setListMeta] = useState({ total: 0, last_page: 1 });
  const [addedProductIds, setAddedProductIds] = useState([]);
  const [cartNotice, setCartNotice] = useState({ open: false, message: "" });

  const queryBrandId = searchParams.get("brand_id")?.trim() ?? "";
  const stateBrandId =
    location.state?.brand?.id != null ? String(location.state.brand.id) : "";
  const resolvedBrandId =
    stateBrandId || (brandId ? String(brandId) : "") || queryBrandId;

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname, currentPage]);

  useEffect(() => {
    setCurrentPage(1);
  }, [resolvedBrandId]);

  useEffect(() => {
    let isMounted = true;

    const loadBrandName = async () => {
      if (!resolvedBrandId) return;
      if (location.state?.brand?.name) {
        setBrandName(location.state.brand.name);
        return;
      }

      try {
        const found = await findBrandById(resolvedBrandId);
        if (isMounted && found?.name) setBrandName(found.name);
      } catch {
        // silent brand name fallback
      }
    };

    loadBrandName();
    return () => {
      isMounted = false;
    };
  }, [resolvedBrandId, location.state?.brand?.name]);

  useEffect(() => {
    let isMounted = true;

    const loadProducts = async () => {
      if (!resolvedBrandId) {
        if (isMounted) {
          setItems([]);
          setListMeta({ total: 0, last_page: 1 });
          setLoading(false);
          setError("");
        }
        return;
      }

      try {
        setLoading(true);
        const result = await fetchProductsPage({
          brandId: resolvedBrandId,
          page: currentPage,
          perPage: PRODUCT_LIST_PAGE_SIZE,
        });

        if (isMounted) {
          setItems(
            result.data.filter((product) => product?.is_active !== false),
          );
          const lastPage = Math.max(1, Number(result.meta?.last_page) || 1);
          setListMeta({
            total: Number(result.meta?.total) || result.data.length,
            last_page: lastPage,
          });
          setError("");
        }
      } catch {
        if (isMounted) {
          setItems([]);
          setListMeta({ total: 0, last_page: 1 });
          setError("Unable to load products right now.");
        }
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    loadProducts();
    return () => {
      isMounted = false;
    };
  }, [resolvedBrandId, currentPage]);

  const products = items;
  const totalCount = Number(listMeta.total) || 0;
  const totalPages = Math.max(1, Number(listMeta.last_page) || 1);

  useEffect(() => {
    setCurrentPage((prev) => Math.min(prev, totalPages));
  }, [totalPages]);

  const handleAddToCart = (product) => {
    if (!product?.id) return;
    dispatch(addToCart(productToCartItem(product, getProductImage)));
    setAddedProductIds((prev) =>
      prev.includes(product.id) ? prev : [...prev, product.id],
    );
    const label = (product?.name ?? "Item").trim() || "Item";
    setCartNotice({ open: true, message: `${label} added to cart` });
  };

  const openProductDetails = (product) => {
    if (!product?.id) return;
    navigate(`/products/${product.id}`, {
      state: {
        product,
        from: `${location.pathname}${location.search}`,
      },
    });
  };

  return pageShell(
    <>
      <Box>
        <Button
          component={Link}
          to="/Brands"
          sx={{
            textTransform: "none",
            mb: 1.75,
            px: 0,
            color: "#334155",
            fontWeight: 600,
          }}
        >
          Back to brands
        </Button>

        <Box
          sx={{
            mb: 2.5,
            p: { xs: 1.5, md: 2.2 },
            border: "1px solid #dbe4f0",
            borderRadius: 2.5,
            background:
              "linear-gradient(120deg, #0f2947 0%, #173d66 58%, #1f4f83 100%)",
            color: "#ffffff",
            boxShadow: "0 14px 28px rgba(15, 41, 71, 0.2)",
            textAlign: "center",
          }}
        >
          <Typography
            sx={{
              fontSize: { xs: 22, md: 28 },
              fontWeight: 800,
              letterSpacing: 0.2,
              display: "block",
            }}
          >
            {brandName || `Brand #${resolvedBrandId || "-"}`}
          </Typography>
          <Typography
            sx={{
              fontSize: 14,
              color: "rgba(255,255,255,0.9)",
              fontWeight: 500,
              mt: 0.55,
              display: "block",
            }}
          >
            {loading ? "Loading products..." : `${totalCount} product(s) found`}
          </Typography>
        </Box>

        {error ? (
          <Card
            elevation={0}
            sx={{
              border: "1px solid #f7d4d4",
              bgcolor: "#fff5f5",
              borderRadius: 2,
              p: 1.4,
              mb: 1.5,
            }}
          >
            <Typography color="error" sx={{ fontWeight: 600 }}>
              {error}
            </Typography>
          </Card>
        ) : null}
        {!resolvedBrandId && !loading ? (
          <Card
            elevation={0}
            sx={{
              border: "1px solid #f7d4d4",
              bgcolor: "#fff5f5",
              borderRadius: 2,
              p: 1.4,
              mb: 1.5,
            }}
          >
            <Typography color="error" sx={{ fontWeight: 600 }}>
              Missing brand id in URL.
            </Typography>
          </Card>
        ) : null}

        {!error && resolvedBrandId ? (
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "repeat(2, minmax(0, 1fr))",
                sm: "repeat(2, minmax(0, 1fr))",
                md: "repeat(3, minmax(0, 1fr))",
                lg: "repeat(5, minmax(0, 1fr))",
              },
              gap: { xs: 1, md: 1.5 },
            }}
          >
            {products.map((product) => (
              <Card
                key={product.id}
                elevation={0}
                sx={{
                  border: "1px solid #e4e9f0",
                  borderRadius: 2,
                  p: { xs: 0.75, md: 1.1 },
                  minHeight: { xs: 280, md: 360 },
                  backgroundColor: "#ffffff",
                  transition: "all 0.2s ease",
                  display: "flex",
                  flexDirection: "column",
                  "&:hover": {
                    transform: "translateY(-2px)",
                    boxShadow: "0 12px 24px rgba(15, 41, 71, 0.09)",
                    borderColor: "#d6dfeb",
                  },
                }}
              >
                <Box
                  sx={{
                    height: { xs: 112, sm: 150, md: 180 },
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    mb: { xs: 0.75, md: 1 },
                    border: "1px solid #edf1f6",
                    borderRadius: 1,
                    bgcolor: "#ffffff",
                    p: { xs: 0.5, md: 1 },
                    cursor: "pointer",
                  }}
                  onClick={() => openProductDetails(product)}
                >
                  {getProductImage(product) ? (
                    <img
                      src={getProductImage(product)}
                      alt={product.name}
                      style={{
                        maxWidth: "100%",
                        maxHeight: "100%",
                        objectFit: "contain",
                      }}
                    />
                  ) : (
                    <Typography sx={{ color: "#98a1af", fontSize: 13 }}>
                      No image
                    </Typography>
                  )}
                </Box>

                <Typography
                  sx={{
                    fontSize: { xs: 10.5, sm: 12, md: 13.5 },
                    fontWeight: 700,
                    color: "#0f172a",
                    minHeight: { xs: 32, md: 36 },
                    mb: { xs: 0.6, md: 0.9 },
                    lineHeight: 1.25,
                    textAlign: "center",
                    textTransform: "uppercase",
                    cursor: "pointer",
                    display: "-webkit-box",
                    WebkitLineClamp: 3,
                    WebkitBoxOrient: "vertical",
                    overflow: "hidden",
                  }}
                  onClick={() => openProductDetails(product)}
                >
                  {product.name}
                </Typography>

                <Box
                  sx={{
                    mb: { xs: 0.75, md: 1 },
                    display: "grid",
                    gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
                    gap: { xs: 0.35, md: 0.55 },
                  }}
                >
                  <Box
                    sx={{
                      border: "1px solid #e8ebf1",
                      borderRadius: 1,
                      bgcolor: "#f8fafc",
                      py: { xs: 0.3, md: 0.45 },
                      px: { xs: 0.35, md: 0.6 },
                      textAlign: "center",
                    }}
                  >
                    <Typography
                      sx={{ fontSize: { xs: 9, md: 10.5 }, color: "#667085" }}
                    >
                      Barcode
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: { xs: 10, md: 11.5 },
                        fontWeight: 700,
                        color: "#0f172a",
                      }}
                    >
                      {product?.barcode || product?.sku || "-"}
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      border: "1px solid #e8ebf1",
                      borderRadius: 1,
                      bgcolor: "#f8fafc",
                      py: { xs: 0.3, md: 0.45 },
                      px: { xs: 0.35, md: 0.6 },
                      textAlign: "center",
                    }}
                  >
                    <Typography
                      sx={{ fontSize: { xs: 9, md: 10.5 }, color: "#667085" }}
                    >
                      Case Size
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: { xs: 10, md: 11.5 },
                        fontWeight: 700,
                        color: "#0f172a",
                      }}
                    >
                      {product?.case_size || "-"}
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      border: "1px solid #e8ebf1",
                      borderRadius: 1,
                      bgcolor: "#f8fafc",
                      py: { xs: 0.3, md: 0.45 },
                      px: { xs: 0.35, md: 0.6 },
                      textAlign: "center",
                    }}
                  >
                    <Typography
                      sx={{ fontSize: { xs: 9, md: 10.5 }, color: "#667085" }}
                    >
                      Layer
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: { xs: 10, md: 11.5 },
                        fontWeight: 700,
                        color: "#0f172a",
                      }}
                    >
                      {product?.layer ?? "-"}
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      border: "1px solid #e8ebf1",
                      borderRadius: 1,
                      bgcolor: "#f8fafc",
                      py: { xs: 0.3, md: 0.45 },
                      px: { xs: 0.35, md: 0.6 },
                      textAlign: "center",
                    }}
                  >
                    <Typography
                      sx={{ fontSize: { xs: 9, md: 10.5 }, color: "#667085" }}
                    >
                      Pallet
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: { xs: 10, md: 11.5 },
                        fontWeight: 700,
                        color: "#0f172a",
                      }}
                    >
                      {product?.pallet ?? "-"}
                    </Typography>
                  </Box>
                </Box>

                <Box sx={{ mt: "auto" }}>
                  <Button
                    variant="contained"
                    size="small"
                    fullWidth
                    onClick={() => handleAddToCart(product)}
                    startIcon={
                      addedProductIds.includes(product.id) ? (
                        <CheckCircleOutlineIcon sx={{ fontSize: 18 }} />
                      ) : (
                        <AddShoppingCartOutlinedIcon sx={{ fontSize: 18 }} />
                      )
                    }
                    sx={{
                      textTransform: "none",
                      fontWeight: 700,
                      borderRadius: 1.5,
                      py: 0.7,
                      bgcolor: PRODUCT_CARD_RED,
                      boxShadow: "0 4px 12px rgba(227, 24, 55, 0.28)",
                      "&:hover": {
                        bgcolor: "#c8102e",
                        boxShadow: "0 6px 16px rgba(227, 24, 55, 0.34)",
                      },
                    }}
                  >
                    {addedProductIds.includes(product.id)
                      ? "Added"
                      : "Add to Quote"}
                  </Button>
                </Box>
              </Card>
            ))}
          </Box>
        ) : null}

        {!loading &&
        !error &&
        resolvedBrandId &&
        totalCount > 0 &&
        totalPages > 1 ? (
          <Box sx={{ display: "flex", justifyContent: "center", mt: 2.5 }}>
            <Pagination
              count={totalPages}
              page={Math.min(currentPage, totalPages)}
              onChange={(_, page) => setCurrentPage(page)}
              color="primary"
              shape="rounded"
              sx={{
                "& .MuiPagination-ul": {
                  gap: 0.4,
                },
                "& .MuiPaginationItem-root": {
                  minWidth: 34,
                  height: 34,
                  borderRadius: 1.3,
                  border: "1px solid #d7dfeb",
                  color: "#1e293b",
                  fontWeight: 700,
                  fontSize: 13,
                  transition: "all 0.2s ease",
                  "&:hover": {
                    borderColor: "#0b3f7b",
                    backgroundColor: "rgba(11, 63, 123, 0.08)",
                  },
                },
                "& .MuiPaginationItem-root.Mui-selected": {
                  color: "#ffffff",
                  borderColor: "#0b3f7b",
                  background:
                    "linear-gradient(90deg, #0f2a7a 0%, #111b4f 100%)",
                  boxShadow: "0 6px 14px rgba(15, 42, 122, 0.24)",
                  "&:hover": {
                    background:
                      "linear-gradient(90deg, #0c2365 0%, #0d163f 100%)",
                  },
                },
              }}
            />
          </Box>
        ) : null}

        {!loading && !error && resolvedBrandId && totalCount === 0 ? (
          <Card
            elevation={0}
            sx={{
              border: "1px solid #e5e7eb",
              borderRadius: 2,
              p: 2.2,
              mt: 1.5,
              textAlign: "center",
            }}
          >
            <Typography sx={{ fontWeight: 700, color: "#334155", mb: 0.6 }}>
              No products found for this brand.
            </Typography>
            <Typography sx={{ color: "#64748b" }}>
              Try another brand from the brands page.
            </Typography>
          </Card>
        ) : null}
      </Box>

      <Snackbar
        open={cartNotice.open}
        autoHideDuration={4000}
        onClose={() => setCartNotice((n) => ({ ...n, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={() => setCartNotice((n) => ({ ...n, open: false }))}
          severity="success"
          variant="filled"
          sx={{
            width: "100%",
            maxWidth: 480,
            boxShadow: "0 8px 24px rgba(15, 23, 42, 0.15)",
          }}
        >
          {cartNotice.message}
        </Alert>
      </Snackbar>
    </>,
  );
};

export default BrandProductsPage;
